/**
 * Cart Synchronization Hook
 * Handles synchronizing local basket state with Shopify cart
 */

import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import type { BasketItem } from "@/stores/basket-store";
import { useBasketStore } from "@/stores/basket-store";
import { orpc } from "@/utils/orpc";

interface CartLineMapping {
  basketItemKey: string; // Local basket item key (id or id-variantId)
  cartLineId: string; // Shopify cart line ID
  merchandiseId: string; // Shopify variant ID
  currentQuantity: number;
}

export interface CartSyncState {
  isSyncing: boolean;
  lastSyncAt: number | null;
  pendingUpdates: number;
  error: string | null;
}

/**
 * Hook for synchronizing local basket with Shopify cart
 */
export const useCartSync = () => {
  const cartId = useBasketStore((state) => state.cartId);
  const basketItems = useBasketStore((state) => state.items);
  const setCartId = useBasketStore((state) => state.setCartId);

  // Track cart line mappings
  const cartLineMappings = useRef<Map<string, CartLineMapping>>(new Map());
  const syncState = useRef<CartSyncState>({
    isSyncing: false,
    lastSyncAt: null,
    pendingUpdates: 0,
    error: null,
  });

  /**
   * Create a new Shopify cart with current basket items
   */
  const createShopifyCart = useCallback(
    async (items: BasketItem[]): Promise<string | null> => {
      try {
        console.log(
          "🛒 [CART SYNC] Creating new Shopify cart with",
          items.length,
          "items"
        );

        // Convert basket items to cart lines
        const lines = items.map((item) => {
          // Ensure we have a valid variant ID
          if (!item.variant?.id) {
            console.error("❌ [CART SYNC] Missing variant ID for item:", {
              itemId: item.id,
              itemName: item.name,
              hasVariant: !!item.variant,
            });
            throw new Error(`Missing variant ID for item: ${item.name}`);
          }

          // Validate variant ID format
          if (!item.variant.id.startsWith("gid://shopify/ProductVariant/")) {
            console.error("❌ [CART SYNC] Invalid variant ID format:", {
              variantId: item.variant.id,
              itemName: item.name,
            });
            throw new Error(`Invalid variant ID format for item: ${item.name}`);
          }

          return {
            merchandiseId: item.variant.id,
            quantity: item.quantity,
          };
        });

        const result = await orpc.shopify.createCart.call({
          lines,
          buyerIdentity: {
            email: "customer@example.com",
            countryCode: "GB",
          },
        });

        if (!result.cart?.id) {
          throw new Error("Failed to create cart");
        }

        // Update cart line mappings
        cartLineMappings.current.clear();
        result.cart.lines?.forEach((line: any) => {
          const basketItemKey = findBasketItemKey(items, line.merchandise.id);
          if (basketItemKey) {
            cartLineMappings.current.set(basketItemKey, {
              basketItemKey,
              cartLineId: line.id,
              merchandiseId: line.merchandise.id,
              currentQuantity: line.quantity,
            });
          }
        });

        console.log("✅ [CART SYNC] Cart created:", result.cart.id);
        return result.cart.id;
      } catch (error) {
        console.error("❌ [CART SYNC] Failed to create cart:", error);
        return null;
      }
    },
    []
  );

  /**
   * Update existing Shopify cart with quantity changes
   */
  const updateShopifyCart = useCallback(
    async (cartId: string, updates: CartLineMapping[]): Promise<boolean> => {
      try {
        console.log(
          "🔄 [CART SYNC] Updating cart",
          cartId,
          "with",
          updates.length,
          "changes"
        );

        const lines = updates.map((update) => ({
          id: update.cartLineId,
          quantity: update.currentQuantity,
        }));

        const result = await orpc.shopify.updateCartLines.call({
          cartId,
          lines,
        });

        if (result.userErrors?.length > 0) {
          throw new Error(result.userErrors[0].message);
        }

        console.log("✅ [CART SYNC] Cart updated successfully");
        return true;
      } catch (error) {
        console.error("❌ [CART SYNC] Failed to update cart:", error);
        return false;
      }
    },
    []
  );

  /**
   * Synchronize current basket state with Shopify cart
   */
  const syncWithShopify = useCallback(async (): Promise<boolean> => {
    if (syncState.current.isSyncing) {
      console.log("⏳ [CART SYNC] Sync already in progress, skipping");
      return false;
    }

    try {
      syncState.current.isSyncing = true;
      syncState.current.error = null;

      // If no cart ID, create new cart
      if (!cartId) {
        const newCartId = await createShopifyCart(basketItems);
        if (newCartId) {
          setCartId(newCartId);
          syncState.current.lastSyncAt = Date.now();
          return true;
        }
        console.error("❌ [CART SYNC] Failed to create new cart");
        return false;
      }

      // Check for quantity differences and missing items
      const updates: CartLineMapping[] = [];
      const missingItems: BasketItem[] = [];

      basketItems.forEach((item) => {
        const itemKey = item.variant?.id
          ? `${item.id}-${item.variant.id}`
          : item.id;
        const mapping = cartLineMappings.current.get(itemKey);

        if (mapping) {
          // Item exists in cart, check if quantity changed
          if (mapping.currentQuantity !== item.quantity) {
            updates.push({
              ...mapping,
              currentQuantity: item.quantity,
            });
          }
        } else {
          // Item not found in mappings - might be a new item or mapping issue
          missingItems.push(item);
        }
      });

      // If we have missing items, recreate the cart to ensure consistency
      if (missingItems.length > 0) {
        const newCartId = await createShopifyCart(basketItems);
        if (newCartId) {
          setCartId(newCartId);
          syncState.current.lastSyncAt = Date.now();
          return true;
        }
        console.error("❌ [CART SYNC] Failed to recreate cart");
        return false;
      }

      if (updates.length > 0) {
        const success = await updateShopifyCart(cartId, updates);
        if (success) {
          // Update mappings with new quantities
          updates.forEach((update) => {
            cartLineMappings.current.set(update.basketItemKey, update);
          });
          syncState.current.lastSyncAt = Date.now();
          return true;
        }
        console.error("❌ [CART SYNC] Failed to update quantities");
        return false;
      }

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error("💥 [CART SYNC] Sync failed:", errorMessage);
      syncState.current.error = errorMessage;
      return false;
    } finally {
      syncState.current.isSyncing = false;
    }
  }, [cartId, basketItems, createShopifyCart, updateShopifyCart, setCartId]);

  /**
   * Force a full resync by recreating the cart
   */
  const forceResync = useCallback(async (): Promise<boolean> => {
    // Clear existing cart ID to force recreation
    setCartId(null);
    cartLineMappings.current.clear();

    return await syncWithShopify();
  }, [syncWithShopify, setCartId]);

  /**
   * Get current sync state
   */
  const getSyncState = useCallback((): CartSyncState => {
    return { ...syncState.current };
  }, []);

  return {
    syncWithShopify,
    forceResync,
    getSyncState,
    cartLineMappings: cartLineMappings.current,
  };
};

/**
 * Helper function to find basket item key by merchandise ID
 */
function findBasketItemKey(
  items: BasketItem[],
  merchandiseId: string
): string | null {
  for (const item of items) {
    const expectedMerchandiseId =
      item.variant?.id || `gid://shopify/ProductVariant/${item.id}`;
    if (expectedMerchandiseId === merchandiseId) {
      return item.variant?.id ? `${item.id}-${item.variant.id}` : item.id;
    }
  }
  return null;
}

/**
 * Auto-sync hook that triggers sync when basket changes
 */
export const useAutoCartSync = (enabled = true) => {
  const { syncWithShopify } = useCartSync();
  const basketItems = useBasketStore((state) => state.items);

  // Debounce sync to avoid too many API calls
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule sync after 1 second of inactivity
    syncTimeoutRef.current = setTimeout(() => {
      if (basketItems.length > 0) {
        console.log(
          "🔄 [AUTO SYNC] Triggering cart sync due to basket changes"
        );
        syncWithShopify().catch((error) => {
          console.error("❌ [AUTO SYNC] Failed:", error);
          toast.error("Failed to sync cart", {
            description:
              "Your cart may not be up to date. Please refresh the page.",
          });
        });
      }
    }, 1000);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [basketItems, enabled, syncWithShopify]);
};
